import { createGlobalStyle } from 'styled-components';
import { theme } from './theme';

export const GlobalStyles = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    scroll-behavior: smooth;
    
    /* Improve mobile performance */
    -webkit-text-size-adjust: 100%;
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
  }

  body {
    font-family: ${theme.fonts.primary};
    background-color: ${theme.colors.background};
    color: ${theme.colors.textPrimary};
    line-height: 1.6;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    overflow-x: hidden;
    
    /* Mobile optimizations */
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: none;
    
    /* Prevent zoom on input focus on iOS */
    @media (max-width: ${theme.breakpoints.sm}) {
      font-size: 16px; /* Prevent iOS zoom */
    }
  }

  ::selection {
    background-color: rgba(30, 58, 138, 0.3);
    color: ${theme.colors.textPrimary};
  }

  /* Custom scrollbar */
  ::-webkit-scrollbar {
    width: 8px;
    
    @media (max-width: ${theme.breakpoints.sm}) {
      width: 4px; /* Thinner scrollbar on mobile */
    }
  }

  ::-webkit-scrollbar-track {
    background: ${theme.colors.background};
  }

  ::-webkit-scrollbar-thumb {
    background: ${theme.colors.navyBlue};
    border-radius: ${theme.borderRadius.full};
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${theme.colors.lightNavy};
  }

  a {
    color: inherit;
    text-decoration: none;
    
    /* Better touch targets */
    @media (max-width: ${theme.breakpoints.sm}) {
      min-height: 44px;
      display: inline-flex;
      align-items: center;
    }
  }

  button {
    font-family: inherit;
    cursor: pointer;
    border: none;
    background: none;
    
    /* Better touch targets */
    min-height: 44px;
    min-width: 44px;
    
    /* Touch feedback */
    @media (hover: none) and (pointer: coarse) {
      &:active {
        opacity: 0.8;
      }
    }
  }

  input, textarea, select {
    font-family: inherit;
    
    /* Mobile input optimizations */
    @media (max-width: ${theme.breakpoints.sm}) {
      font-size: 16px; /* Prevent iOS zoom */
      border-radius: 0; /* Remove iOS default styling */
      -webkit-appearance: none;
    }
  }

  /* Focus styles for accessibility */
  *:focus {
    outline: 2px solid ${theme.colors.navyBlue};
    outline-offset: 2px;
  }

  /* Remove focus outline for mouse users */
  *:focus:not(:focus-visible) {
    outline: none;
  }

  /* Ensure focus is visible for keyboard users */
  *:focus-visible {
    outline: 2px solid ${theme.colors.navyBlue};
    outline-offset: 2px;
  }

  /* Smooth animations */
  @keyframes gentle-float {
    0%, 100% {
      transform: translateY(0px) rotate(0deg);
    }
    33% {
      transform: translateY(-10px) rotate(1deg);
    }
    66% {
      transform: translateY(5px) rotate(-1deg);
    }
  }

  @keyframes fade-in-up {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  /* Animation classes */
  .fade-in-up {
    animation: fade-in-up 0.6s ease-out;
  }

  .fade-in {
    animation: fade-in 0.4s ease-out;
  }

  /* Reduce motion for users who prefer it */
  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }

  /* Mobile-specific utilities */
  @media (max-width: ${theme.breakpoints.sm}) {
    /* Prevent horizontal scroll */
    .container {
      max-width: 100vw;
      overflow-x: hidden;
    }
    
    /* Better spacing for mobile */
    .mobile-spacing {
      padding-left: ${theme.spacing.lg};
      padding-right: ${theme.spacing.lg};
    }
    
    /* Safe area support for notched devices */
    .safe-area-top {
      padding-top: env(safe-area-inset-top);
    }
    
    .safe-area-bottom {
      padding-bottom: env(safe-area-inset-bottom);
    }
    
    .safe-area-left {
      padding-left: env(safe-area-inset-left);
    }
    
    .safe-area-right {
      padding-right: env(safe-area-inset-right);
    }
  }

  /* Screen reader only content */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  /* Skip link styling */
  .skip-link:focus {
    position: absolute !important;
    top: 6px !important;
    left: 6px !important;
    z-index: 1001;
    background: ${theme.colors.navyBlue} !important;
    color: white !important;
    padding: ${theme.spacing.sm} ${theme.spacing.md} !important;
    text-decoration: none !important;
    border-radius: ${theme.borderRadius.md} !important;
    font-weight: ${theme.fontWeights.medium} !important;
    box-shadow: 0 4px 12px rgba(30, 58, 138, 0.3) !important;
  }

  /* High contrast mode support */
  @media (prefers-contrast: high) {
    * {
      border-color: currentColor !important;
    }
  }

  /* Dark mode support (if system preference changes) */
  @media (prefers-color-scheme: dark) {
    /* Our theme is already dark, but we can enhance it */
    ::selection {
      background-color: rgba(30, 58, 138, 0.5);
    }
  }
`;

// Export theme for use in components
export { theme }; 